import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TextFieldMask } from '../../utils/TextfieldMask/TextfieldMask';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams } from 'react-router-dom';
import { leadProcessoStatus } from '../../constants/crm';
import { createLead } from '../../controllers/crm/leadController';
import { Copyright } from '../Copyright';

// Background de imagem com overlay para melhorar legibilidade do texto
const FormBackground = styled(Box)(({ theme }) => ({
  // backgroundImage: 'url("/assets/images/background-imoveis.jpg")',
  // Fallback para uma URL pública caso a imagem local não esteja disponível
  backgroundImage: `url("/assets/images/background-imoveis.jpg"), url("https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1
  }
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  opacity: 1,
  borderRadius: '16px',
  width: '100%',
  maxWidth: '500px',
  position: 'relative', // Garantir posicionamento correto
  zIndex: 2, // Manter o z-index, mas garantir que esteja acima do overlay
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  }
}));

const LeadsForm2 = ({ onSubmit }) => {
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [erros, setErros] = useState({ nome: false, telefone: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);
  // Estado adicional para detectar se os dados foram autopreenchidos
  const [autoPreenchido, setAutoPreenchido] = useState(false);
  const [hasWhatsapp, setHasWhatsapp] = useState(true);
  const [message, setMessage] = useState('')
  const [hasErro, setHasErro] = useState(false)

  let processo = leadProcessoStatus.Automatico
  const fontTitMobile = '0.9rem'
  const fontTitNormal = '1rem'
  const mtmob = 1
  const mbmob = 0
  const mt = 2
  const mb = 2

  // Adiciona dependências para evitar warnings

  // Modifica o handleSubmit para salvar os dados para futuro autopreenchimento
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validação básica
    const novosErros = {
      nome: nome.trim() === '',
      telefone: telefone.length < 14 // Considerando máscara (99) 99999-9999
    };

    setErros(novosErros);

    if (!novosErros.nome && !novosErros.telefone && onSubmit) {
      try {
        setIsSubmitting(true);
        const data = {
          id1: params.id1,
          id2: params.id2,
          id3: params.id3,
          id4: params.id4,
          id5: params.id5,
          nome: nome,
          telefone: telefone,
          email: '', // Email não é mais obrigatório
          interesse: '',
          processo: processo,
          whatsapp: hasWhatsapp
        };
        const result = await createLead(data);
        if (result.status === 200) { // Lead distribuído
          setHasErro(false)
        } else if (result.status === 223) { // Erro de Chave
          setHasErro(true)
          setMessage(result.data.message)
        } else if (result.status === 250) { // Lead não distribuído
          setHasErro(false)
        }
        else {
          setHasErro(true)
          setMessage(result.data.message)
        }
        setFormCompleted(true);
      } catch (error) {
        console.error('Erro ao enviar formulário:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto' // Permite rolagem se necessário
    }}>
      <FormBackground>
        <Container
          maxWidth="sm"
          sx={{
            position: 'relative',
            zIndex: 10 // Garantir que o container esteja acima de outros elementos
          }}
        >
          <FormContainer elevation={6}>
            {formCompleted ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
              }}>
                {!hasErro && <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />}
                {hasErro && <CheckCircleOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />}
                <Typography
                  variant="h5"
                  align="center"
                  color="primary"
                  gutterBottom
                  fontWeight="bold"
                >
                  {message ? message : 'Recebemos sua solicitação!'}
                </Typography>
                {!hasErro && (
                  <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                    Entraremos em contato em breve para te apresentar as melhores opções de imóveis.
                  </Typography>
                )}
                {hasErro && (
                  <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                    Verifique os dados informados!
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  align="center"
                  color="primary"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ mb: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <HomeWorkIcon sx={{ mr: 1, fontSize: isMobile ? fontTitMobile : fontTitNormal }} />
                  Encontre Seu Imóvel Ideal
                </Typography>

                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{
                    mt: isMobile ? mtmob : mt,
                    position: 'relative',
                    zIndex: 20 // Garantir que os controles do formulário estejam acessíveis
                  }}
                >
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="nome"
                    label="Nome"
                    name="nome"
                    autoComplete="name" // Suporte ao preenchimento automático do navegador
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    error={erros.nome}
                    helperText={erros.nome ? "Informe um nome!" : autoPreenchido ? "Dados preenchidos automaticamente!" : ""}
                    variant="outlined"
                    InputProps={{
                      sx: { bgcolor: autoPreenchido ? 'rgba(76, 175, 80, 0.1)' : 'transparent' }
                    }}
                    sx={{
                      mb: isMobile ? mbmob : mb,
                      pointerEvents: 'auto',
                      '& .MuiInputBase-input': {
                        cursor: 'text'
                      }
                    }}
                    disabled={isSubmitting}
                  />

                  <Box sx={{ position: 'relative' }}>
                    <TextFieldMask
                      margin="normal"
                      required
                      fullWidth
                      id="telefone"
                      label="Telefone"
                      name="telefone"
                      placeholder="(99) 99999-9999"
                      autoComplete="tel" // Suporte ao preenchimento automático do navegador
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      error={erros.telefone}
                      helperText={erros.telefone ? "Informe um telefone válido!" : autoPreenchido ? "Dados preenchidos automaticamente!" : ""}
                      mask="phone"
                      variant="outlined"
                      InputProps={{
                        sx: { bgcolor: autoPreenchido ? 'rgba(76, 175, 80, 0.1)' : 'transparent' }
                      }}
                      sx={{
                        mb: isMobile ? mbmob : mb,
                        pointerEvents: 'auto',
                        '& .MuiInputBase-input': {
                          cursor: 'text'
                        }
                      }}
                      disabled={isSubmitting}
                    />

                    {/* Novo checkbox para WhatsApp */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        mt: 1.5,
                        ml: 1
                      }}
                    >
                      <Box
                        component="label"
                        htmlFor="whatsapp-check"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          userSelect: 'none',
                          pointerEvents: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          id="whatsapp-check"
                          checked={hasWhatsapp}
                          onChange={(e) => setHasWhatsapp(e.target.checked)}
                          disabled={isSubmitting}
                          style={{
                            marginRight: '8px',
                            cursor: 'pointer'
                          }}
                        />
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: hasWhatsapp ? 'success.main' : 'text.secondary',
                            fontSize: isMobile ? fontTitMobile : fontTitNormal,
                            fontWeight: hasWhatsapp ? 500 : 400,
                            '& svg': {
                              ml: 0.5,
                              color: hasWhatsapp ? '#25D366' : 'text.disabled'
                            }
                          }}
                        >
                          Este telefone tem WhatsApp
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {autoPreenchido && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Detectamos seus dados e preenchemos automaticamente o formulário para sua conveniência.
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    onClick={(e) => {

                      handleSubmit(e);
                    }}
                    sx={{
                      mt: isMobile ? mtmob : mt,
                      py: isMobile ? 1 : 1.5,
                      fontSize: isMobile ? fontTitMobile : fontTitNormal,
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      textTransform: 'none',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      pointerEvents: 'auto' // Garantir que os eventos do mouse funcionem
                    }}
                  >
                    {isSubmitting ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                        Enviando...
                      </Box>
                    ) : (
                      'Solicitar Contato'
                    )}
                  </Button>

                  <Typography variant="body2" align="center"
                    sx={{ mt: isMobile ? mtmob : mt, color: 'text.secondary' }}
                  >
                    Entraremos em contato para apresentar as melhores opções para você
                  </Typography>
                </Box>
              </>
            )}
          </FormContainer>
          <Stack sx={{ m: 1, textAlign: 'center' }}>
            <Copyright />
          </Stack>
        </Container>
      </FormBackground>
    </Box>
  );
};

export default LeadsForm2;
